/**
 * Infinite Products Hook
 *
 * Wraps `useInfiniteQuery` for seamless infinite scrolling on the product page.
 *
 * Memory optimizations:
 *  - `maxPages` – TanStack Query v5 natively drops the oldest cached pages
 *    once this limit is exceeded, keeping JS heap bounded.
 *  - Pair with `@tanstack/react-virtual` on the UI side so only visible
 *    DOM rows are rendered.
 */

import { useInfiniteQuery } from "@tanstack/react-query";
import type { Product, ProductSortType, ProductFilters } from "./types";
import { transformRawProducts } from "./transformations";
import { PRODUCT_QUERY_OPTIONS } from "./queryKeys";
import { getProducts } from "../../services/products.service";
import type { GetProductsPayload } from "../../services/products.service";

// ============================================================================
// TYPES
// ============================================================================

export interface UseInfiniteProductsOptions {
  categoryId?: number;
  sortType?: ProductSortType;
  pageSize?: number;
  filters?: ProductFilters;
  enabled?: boolean;

  /**
   * Maximum number of pages to keep in the React Query cache.
   * Older pages are dropped to save memory on low-RAM devices.
   * Set to `undefined` to keep all pages (default: 5).
   */
  maxPages?: number;
}

interface InfiniteProductsPage {
  products: Product[];
  totalCount: number;
  hasMore: boolean;
  /** The offset that should be used for the *next* page fetch. */
  nextOffset: number;
}

export interface UseInfiniteProductsResult {
  /** All products across every fetched page, flattened into a single array. */
  products: Product[];
  totalCount: number;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingPreviousPage: boolean;
  hasPreviousPage: boolean;
  fetchPreviousPage: () => void;
  /** Offset of the first page currently in cache (0 when no top eviction). */
  firstPageOffset: number;
  isError: boolean;
  error: Error | null;
}

// ============================================================================
// SORT MAPPING (duplicated from useProducts to stay self-contained)
// ============================================================================

function mapSortAndFilters(
  sortType: ProductSortType | undefined,
  filters?: ProductFilters,
): Pick<
  GetProductsPayload,
  "sort_by" | "sort_order" | "is_featured" | "is_on_sale"
> {
  const result: Pick<
    GetProductsPayload,
    "sort_by" | "sort_order" | "is_featured" | "is_on_sale"
  > = {};

  switch (sortType) {
    case "newest":
      result.sort_by = "created_at";
      result.sort_order = "desc";
      break;
    case "popular":
      result.sort_by = "popularity";
      result.sort_order = "desc";
      break;
    case "price_low_to_high":
      result.sort_by = "price";
      result.sort_order = "asc";
      break;
    case "price_high_to_low":
      result.sort_by = "price";
      result.sort_order = "desc";
      break;
    default:
      result.sort_by = "created_at";
      result.sort_order = "desc";
  }

  if (filters?.isFeatured) result.is_featured = true;
  if (filters?.isOnSale) result.is_on_sale = true;

  return result;
}

// ============================================================================
// HOOK
// ============================================================================

export function useInfiniteProducts(
  options: UseInfiniteProductsOptions = {},
): UseInfiniteProductsResult {
  const {
    categoryId,
    sortType = "newest",
    pageSize = 20,
    filters = {},
    enabled = true,
    maxPages = 5,
  } = options;

  const queryKey = [
    "products",
    "infinite",
    {
      categoryId,
      sortType,
      pageSize,
      filters,
    },
  ];

  const query = useInfiniteQuery<InfiniteProductsPage>({
    queryKey,

    queryFn: async ({ pageParam }): Promise<InfiniteProductsPage> => {
      const offset = pageParam as number;
      const sortAndFilters = mapSortAndFilters(sortType, filters);

      const payload: GetProductsPayload = {
        limit: pageSize,
        offset,
        ...sortAndFilters,
      };

      if (categoryId !== undefined && categoryId !== null) {
        payload.category_id = categoryId;
      }

      if (filters.priceRange) {
        if (filters.priceRange.min !== undefined)
          payload.min_price = filters.priceRange.min;
        if (filters.priceRange.max !== undefined)
          payload.max_price = filters.priceRange.max;
      }

      if (filters.brands && filters.brands.length > 0) {
        const firstBrandId = parseInt(filters.brands[0], 10);
        if (!isNaN(firstBrandId)) payload.brand_id = firstBrandId;
      }

      if (filters.productTypes && filters.productTypes.length > 0) {
        const firstTypeId = parseInt(filters.productTypes[0], 10);
        if (!isNaN(firstTypeId)) payload.product_type_id = firstTypeId;
      }

      if (filters.searchQuery) {
        payload.search = filters.searchQuery;
      }

      const result = await getProducts(payload);

      const products = transformRawProducts(result.products as any[], {
        sortType: sortType || "newest",
      });

      return {
        products,
        totalCount: result.totalCount,
        hasMore: result.hasMore,
        nextOffset: offset + pageSize,
      };
    },

    initialPageParam: 0,

    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextOffset : undefined,

    getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
      const prev = (firstPageParam as number) - pageSize;
      return prev >= 0 ? prev : undefined;
    },

    // ── Memory cap ──────────────────────────────────────────────
    // When the user scrolls far, old pages are evicted from the
    // query cache.  Re-scrolling up will re-fetch them on demand.
    maxPages,

    enabled,
    staleTime: PRODUCT_QUERY_OPTIONS.lists.staleTime,
    gcTime: PRODUCT_QUERY_OPTIONS.lists.gcTime,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: PRODUCT_QUERY_OPTIONS.lists.retry,
  });

  // Flatten pages into a single products array
  const products = query.data?.pages.flatMap((page) => page.products) ?? [];

  const totalCount = query.data?.pages[0]?.totalCount ?? 0;

  const firstPage = query.data?.pages[0];
  const firstPageOffset = firstPage
    ? Math.max(0, firstPage.nextOffset - pageSize)
    : 0;

  return {
    products,
    totalCount,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingPreviousPage: query.isFetchingPreviousPage,
    hasPreviousPage: query.hasPreviousPage,
    fetchPreviousPage: query.fetchPreviousPage,
    firstPageOffset,
    isError: query.isError,
    error: query.error as Error | null,
  };
}
